import {
  User,
  COLLECTIONS,
} from '@bookstore/shared';
import {
  signInWithPopup,
  GoogleAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFirebaseAuth ,  getFirebaseFirestore} from '@bookstore/shared/browser';

// import { PhoneAuthProvider, signInWithCredential } from 'firebase/auth';
// import { httpsCallable} from 'firebase/functions';
// import { getFirebaseFunctions } from '@bookstore/shared';

export class AuthService {
  private get auth() {
    return getFirebaseAuth();
  }
  private get db() {
    return getFirebaseFirestore();
  }
  // private get functions() {
  //   return getFirebaseFunctions();
  // }
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private confirmationResult: ConfirmationResult | null = null;

  /**
   * Initialize reCAPTCHA verifier for phone authentication
   * @param containerId - ID of the container element for reCAPTCHA
   */
  initializeRecaptcha(containerId: string): void {
    // Check if element exists in DOM
    const container = document.getElementById(containerId);
    if (!container) {
      console.warn('reCAPTCHA container element not found');
      return;
    }

    // If verifier exists but element was removed (after navigation), reset it
    if (this.recaptchaVerifier) {
      try {
        // Check if the verifier's element still exists
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = null;
      } catch (e) {
        // Element was removed, just reset
        this.recaptchaVerifier = null;
      }
    }

    // Clear the container element to avoid "already rendered" error
    container.innerHTML = '';

    // Create new verifier
    this.recaptchaVerifier = new RecaptchaVerifier(this.auth, containerId, {
      size: 'invisible',
      callback: () => {
        console.log('reCAPTCHA solved');
      },
      'expired-callback': () => {
        console.warn('reCAPTCHA expired. Resetting verifier...');
        if (this.recaptchaVerifier) {
          this.recaptchaVerifier.clear();
        }
        this.recaptchaVerifier = null;
      },
    });
  }

  /**
   * Reset reCAPTCHA verifier (call on component unmount)
   */
  resetRecaptcha(): void {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
      } catch (e) {
        // Ignore error if element was already removed
      }
      this.recaptchaVerifier = null;
    }
  }

  /**
   * Send OTP to phone number using BSNL api
   * @param phoneNumber - Phone number with country code (e.g., +911234567890)
   */
  // async sendOTP(phoneNumber: string): Promise<void> {
  //   try {

  //     // Validate phone number format
  //     const phoneRegex = /^\+91[6-9]\d{9}$/;
  //     if (!phoneRegex.test(phoneNumber)) {
  //       throw new Error('Invalid phone number format. Use +91XXXXXXXXXX');
  //     }

  //     // Call Cloud Function to send OTP via BSNL
  //     const sendOTPFunction = httpsCallable(this.functions, 'sendBSNLOTP');
  //     console.log(phoneNumber,"phonenumber");
  //     const result = await sendOTPFunction({ phoneNumber });
  //     const data = result.data as { success: boolean; verificationId: string };

  //     return {
  //       success: data.success,
  //       verificationId: data.verificationId,
  //     };
  //   } catch (error: any) {
  //     console.error('Error sending OTP:', error);
  //     throw new Error(error.message || 'Failed to send OTP');
  //   }
  // }

  /**
   * Confirm OTP and sign in user
   */

  /**
   * Verify OTP and sign in user
   * @param phoneNumber - Phone number
   * @param otp - OTP code
   * @param verificationId - Verification ID from sendOTP
   */
  // async verifyOTP(
  //   phoneNumber: string,
  //   otp: string,
  //   verificationId: string
  // ): Promise<FirebaseUser> {
  //   try {
  //     // Call Cloud Function to verify OTP
  //     const verifyOTPFunction = httpsCallable(this.functions, 'verifyBSNLOTP');
  //     const result = await verifyOTPFunction({
  //       phoneNumber,
  //       otp,
  //       verificationId,
  //     });

  //     const data = result.data as { success: boolean; customToken: string };

  //     if (!data.success || !data.customToken) {
  //       throw new Error('Invalid OTP');
  //     }

  //     // Sign in with custom token
  //     const userCredential = await signInWithCredential(
  //       this.auth,
  //       PhoneAuthProvider.credential(verificationId, otp)
  //     );

  //     // TODO as we are using bsnl we can use : await this.auth.signInWithCustomToken(data.customToken);
  //     /*//  Correct Firebase login using the server-generated custom token
  //     await this.auth.signInWithCustomToken(data.customToken);

  //     const currentUser = this.auth.currentUser;
  //     if (currentUser) {
  //       await this.createOrUpdateUserDocument(currentUser);
  //       return currentUser;
  //     }

  //     throw new Error('Failed to retrieve user after login');
  //     */

  //     // Create or update user document
  //     await this.createOrUpdateUserDocument(userCredential.user);

  //     return userCredential.user;
  //   } catch (error: any) {
  //     console.error('Error verifying OTP:', error);
  //     throw new Error(error.message || 'Failed to verify OTP');
  //   }
  // }

  /**
   * Sign in with Google (Alternative method)
   */
  async signInWithGoogle(): Promise<FirebaseUser> {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');

      const result = await signInWithPopup(this.auth, provider);

      // Create or update user document
      await this.createOrUpdateUserDocument(result.user);

      return result.user;
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      throw new Error(error.message || 'Failed to sign in with Google');
    }
  }

  /**
   * Sign in with Phone Number using Firebase Phone Auth (Fallback)
   */
  async signInWithPhoneNumber(phoneNumber: string): Promise<void> {
    try {
      // Check if verifier exists and is valid
      if (!this.recaptchaVerifier) {
        // Try to initialize if not already done
        this.initializeRecaptcha('recaptcha-container');
        
        if (!this.recaptchaVerifier) {
          throw new Error('reCAPTCHA initialization failed. Please refresh the page.');
        }
      }

      this.confirmationResult = await signInWithPhoneNumber(
        this.auth,
        phoneNumber,
        this.recaptchaVerifier
      );
    } catch (error: any) {
      console.error('Error signing in with phone number:', error);
      
      // If element was removed, reset and ask user to try again
      if (error.message?.includes('element has been removed')) {
        this.recaptchaVerifier = null;
        throw new Error('Session expired. Please try again.');
      }
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to send OTP';
      
      if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many attempts. Please try again after some time.';
      } else if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format.';
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'SMS quota exceeded. Please try again later.';
      } else if (error.code === 'auth/invalid-app-credential') {
        errorMessage = 'Phone authentication not configured. Please contact support.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Confirm phone verification code (Fallback method)
   */
  async confirmVerificationCode(code: string): Promise<FirebaseUser> {
    try {
      if (!this.confirmationResult) {
        throw new Error('No confirmation result available');
      }

      const result = await this.confirmationResult.confirm(code);

      // Create or update user document
      await this.createOrUpdateUserDocument(result.user);

      return result.user;
    } catch (error: any) {
      console.error('Error confirming verification code:', error);
      throw new Error(error.message || 'Invalid verification code');
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(this.auth);
    } catch (error: any) {
      console.error('Error signing out:', error);
      throw new Error(error.message || 'Failed to sign out');
    }
  }

  /**
   * Get current user from Firestore
   */
  async getCurrentUser(uid: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(this.db, COLLECTIONS.USERS, uid));

      if (!userDoc.exists()) {
        return null;
      }

      return { uid, ...userDoc.data() } as User;
    } catch (error: any) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Create or update user document in Firestore
   */
  private async createOrUpdateUserDocument(firebaseUser: FirebaseUser): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, firebaseUser.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // Create new user document
        const newUser: Partial<User> = {
          phoneNumber: firebaseUser.phoneNumber || '',
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || '',
          photoURL: firebaseUser.photoURL || '',
          // role: UserRole.CUSTOMER,
          addresses: [],
          wishlist: [],
          isActive: true,
          createdAt: serverTimestamp() as any,
          updatedAt: serverTimestamp() as any,
        };

        await setDoc(userRef, newUser);
      } else {
        // Update existing user
        await updateDoc(userRef, {
          displayName: firebaseUser.displayName || userDoc.data().displayName,
          photoURL: firebaseUser.photoURL || userDoc.data().photoURL,
          email: firebaseUser.email || userDoc.data().email,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('Error creating/updating user document:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(uid: string, data: Partial<User>): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, uid);
      await updateDoc(userRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      throw new Error(error.message || 'Failed to update profile');
    }
  }

  /**
   * Add address to user profile
   */
  async addAddress(uid: string, address: any): Promise<void> {
    try {
      const userDoc = await this.getCurrentUser(uid);
      if (!userDoc) throw new Error('User not found');

      const addresses = userDoc.addresses || [];

      // If this is the first address or marked as default, set as default
      if (addresses.length === 0 || address.isDefault) {
        addresses.forEach((addr) => (addr.isDefault = false));
      }

      addresses.push({
        ...address,
        id: `addr_${Date.now()}`,
      });

      await this.updateProfile(uid, { addresses } as any);
    } catch (error: any) {
      console.error('Error adding address:', error);
      throw new Error(error.message || 'Failed to add address');
    }
  }

  /**
   * Update FCM token for push notifications
   */
  async updateFCMToken(uid: string, fcmToken: string): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, uid);
      await updateDoc(userRef, { fcmToken });
    } catch (error) {
      console.error('Error updating FCM token:', error);
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
